import {BuildQualifiedContentDescriptor} from "@khan/assignments-utils";
import {
    type CurationNode,
    type FilteredContentDescriptors,
    type ModalNavigator,
    type SelectedContentDescriptors,
    type TopicChild,
    type TopicItem,
    type TopicMap,
    type TopicMapEntry,
} from "@khan/class-types";
import {
    GetFilteredTopicChildren,
    GetQualifiedContentDescriptor,
    IsFilterActive,
} from "@khan/class-utils";

import type {Action} from "./actions.ts";

const getTopicMapWithChildren = (
    id: string,
    children: ReadonlyArray<TopicChild> | null | undefined,
    parentId: string | null | undefined,
    topicMap: TopicMap,
    fetchThreeLevels: boolean,
): TopicMap => {
    const parent = (parentId && topicMap[parentId]) || {};
    const newTopic = {
        // Default selected should be the selected status of its parent. But
        // that should be overwritten if the current topic was selected.
        // @ts-expect-error - TS2783 - 'selected' is specified more than once, so this usage will be overwritten. | TS2339 - Property 'selected' does not exist on type '{}'.
        selected: parent.selected || false,
        // By default, every topic and its descendants are in the filtered set.
        // But that should be overwritten if the current topic was filtered.
        // @ts-expect-error - TS2783 - 'inFilteredSet' is specified more than once, so this usage will be overwritten. | TS2339 - Property
        inFilteredSet: true,
        // @ts-expect-error - TS2783 - 'descendantInFilteredSet' is specified more than once, so this usage will be overwritten. | TS2339 - Property
        descendantInFilteredSet: true,
        parentId,
        ...topicMap[id],
    } as const;

    // We only want to add the children if there's none existing for the
    // current topic or if the request is three levels fetching which has the
    // standards data.
    if (!newTopic.children || fetchThreeLevels) {
        // @ts-expect-error - TS2540 - Cannot assign to 'children' because it is a read-only property.
        newTopic.children = children;
    }

    // Setup the DCUL based on the parent, and then fill in
    // in the next empty spot.  This works because this is a
    // recursive function, drilling down into the children as
    // we go.  Going from course, to units, to lessons.
    // @ts-expect-error - TS2540 - Cannot assign to 'domainId' because it is a read-only property. | TS2339 - Property 'domainId' does not exist on type '{}'.
    newTopic.domainId = parent.domainId;
    // @ts-expect-error - TS2540 - Cannot assign to 'courseId' because it is a read-only property. | TS2339 - Property 'courseId' does not exist on type '{}'.
    newTopic.courseId = parent.courseId;
    // @ts-expect-error - TS2540 - Cannot assign to 'unitId' because it is a read-only property. | TS2339 - Property 'unitId' does not exist on type '{}'.
    newTopic.unitId = parent.unitId;
    // @ts-expect-error - TS2540 - Cannot assign to 'lessonId' because it is a read-only property. | TS2339 - Property 'lessonId' does not exist on type '{}'.
    newTopic.lessonId = parent.lessonId;

    // Set the appropriate DCUL id
    if (!newTopic.courseId) {
        // @ts-expect-error - TS2540 - Cannot assign to 'courseId' because it is a read-only property.
        newTopic.courseId = id;
    } else if (!newTopic.unitId) {
        // @ts-expect-error - TS2540 - Cannot assign to 'unitId' because it is a read-only property.
        newTopic.unitId = id;
    } else if (!newTopic.lessonId) {
        // @ts-expect-error - TS2540 - Cannot assign to 'lessonId' because it is a read-only property.
        newTopic.lessonId = id;
    }

    // Add the current topic before going through the children so that we have
    // the parent correct.
    topicMap[id] = newTopic;

    // Go through the children and add each of them in the topicMap if they are
    // Topic.
    for (const child of children || []) {
        if (child.kind === "Topic") {
            getTopicMapWithChildren(
                child.id,
                child.children,
                id,
                topicMap,
                fetchThreeLevels,
            );
        }
    }

    return topicMap;
};

const getUpdatedChildren = (
    selected: boolean,
    id: string,
    topicMap: TopicMap,
): TopicMap => {
    let childrenMap: any = {};
    const children: TopicMapEntry["children"] = topicMap[id]?.children;
    // If the topic doesn't exist or it doesn't have children,
    // return empty object.
    if (children) {
        for (const child of children) {
            if (child.kind === "Topic") {
                const isVisible: boolean =
                    topicMap[child.id]?.inFilteredSet ||
                    topicMap[child.id]?.descendantInFilteredSet;
                // If the child is Topic and is visible to the user,
                // we want to change its selected state.
                if (isVisible) {
                    childrenMap[child.id] = {
                        ...topicMap[child.id],
                        selected,
                    };
                }
                // Also the child might have children so we should get
                // its updated children.
                childrenMap = {
                    ...childrenMap,
                    ...getUpdatedChildren(selected, child.id, topicMap),
                };
            }
        }
    }
    return childrenMap;
};

const getMaybeCheckedParents = (
    parentId: string | null | undefined,
    topicMap: TopicMap,
    selectedMap: SelectedContentDescriptors,
    filteredContentDescriptors: FilteredContentDescriptors,
    domain: CurationNode,
    filterIsActive: boolean,
): TopicMap => {
    let parentMap: any = {};
    if (parentId) {
        const parent = topicMap[parentId];
        const children: readonly TopicChild[] | null | undefined =
            parent.children;
        // Only filtered children are visible to the user.
        const filteredChildren = children
            ? GetFilteredTopicChildren(
                  children,
                  topicMap,
                  filteredContentDescriptors,
                  parentId,
                  domain,
                  filterIsActive,
              )
            : [];

        // If all visible children are selected, select the parent.
        if (
            filteredChildren.every((child) => {
                if (child.kind === "Topic") {
                    return topicMap[child.id].selected;
                }

                const {kind, contentId} = child;
                const descriptor = `${kind}:${contentId}`;
                const qualifiedContentDescriptor =
                    BuildQualifiedContentDescriptor(
                        parent.domainId,
                        parent.courseId,
                        parent.unitId,
                        parent.lessonId,
                        descriptor,
                    );
                return selectedMap[qualifiedContentDescriptor];
            })
        ) {
            parentMap[parentId] = {
                ...parent,
                selected: true,
            };
            parentMap = {
                ...parentMap,
                ...getMaybeCheckedParents(
                    parent.parentId,
                    {
                        ...topicMap,
                        // We want to consider the current checked parent for
                        // checking children of its parent.
                        ...parentMap,
                    },
                    selectedMap,
                    filteredContentDescriptors,
                    domain,
                    filterIsActive,
                ),
            };
        }
    }
    return parentMap;
};

const getUncheckedParents = (
    parentId: string | null | undefined,
    topicMap: TopicMap,
): TopicMap => {
    // Uncheck the parent and if there is a parent of it, uncheck that as well
    // until there's no parentId.
    let parentMap: any = {};

    if (parentId) {
        const parent = topicMap[parentId];
        parentMap[parentId] = {
            ...parent,
            selected: false,
        };
        parentMap = {
            ...parentMap,
            ...getUncheckedParents(parent.parentId, topicMap),
        };
    }
    return parentMap;
};

// Set all topics to be in the filtered set.
// If a topic has descendants, they will be in the filtered set too.
const clearTopicMapFilter = (topicMap: TopicMap): TopicMap => {
    return Object.fromEntries(
        Object.entries(topicMap).map(([id, topicMapEntry]) => {
            const descendantInFilteredSet = topicMapEntry.children
                ? true
                : false;
            return [
                id,
                {
                    ...topicMapEntry,
                    inFilteredSet: true,
                    descendantInFilteredSet: descendantInFilteredSet,
                },
            ];
        }),
    );
};

// Set all content to be in the filtered set.
const clearFilteredContentDescriptors = (
    filteredContentDescriptors: FilteredContentDescriptors,
): FilteredContentDescriptors => {
    return Object.fromEntries(
        Object.entries(filteredContentDescriptors).map(
            ([contentDescriptor, _]) => [contentDescriptor, true],
        ),
    );
};

// Get all of the content descendants for a given topic.
// Content is returned as qualified content descriptors.
const getFilteredContentChildren = (
    parent: TopicMapEntry,
    child: TopicChild,
    topicMap: TopicMap,
    searchValue: string,
    contentTypes: readonly string[],
    domain,
): Array<{
    descriptor: string;
    inFilteredSet: boolean;
}> => {
    if (child.kind === "Topic") {
        // This topic is a TopicItem.
        if (child.children) {
            // Recurse to get all content children.
            const newParent = topicMap[child.id];
            return child.children
                ?.map((newChild) =>
                    getFilteredContentChildren(
                        newParent,
                        newChild,
                        topicMap,
                        searchValue,
                        contentTypes,
                        domain,
                    ),
                )
                .flat();
        }
        // This topic has no content children.
        return [];
    }
    // This topic is a content item.
    const qualifiedContentDescriptor = GetQualifiedContentDescriptor(
        child,
        parent,
        domain,
    );
    if (!qualifiedContentDescriptor) {
        return [];
    }
    const inFilteredSet = isInFilteredSet(child, searchValue, contentTypes);

    return [
        {
            descriptor: qualifiedContentDescriptor,
            inFilteredSet: inFilteredSet,
        },
    ];
};

// Return true if the TopicChild (topic or content) is in the filtered set.
const isInFilteredSet = (
    item: TopicChild,
    searchValue: string,
    contentTypes: readonly string[],
): boolean => {
    let passesKeywordSearch = false;
    let passesContentTypeFilter = false;

    if (searchValue === "") {
        passesKeywordSearch = true;
    }

    const title: string = item.title || "";

    if (title.toLowerCase().includes(searchValue.toLowerCase())) {
        passesKeywordSearch = true;
    }

    if (contentTypes.length === 0 || contentTypes.includes(item.kind)) {
        passesContentTypeFilter = true;
    }

    return passesKeywordSearch && passesContentTypeFilter;
};

// Return true if any of the given topic's descendants are in the filtered set.
const childInFilteredSet = (
    topic: TopicChild,
    searchValue: string,
    contentTypes: readonly string[],
): boolean => {
    if (topic.kind && topic.kind === "Topic" && topic.children) {
        for (const child of topic.children) {
            // Check this topic's children
            if (isInFilteredSet(child, searchValue, contentTypes)) {
                return true;
            }
            // Recurse to check this topic's grandchildren
            if (childInFilteredSet(child, searchValue, contentTypes)) {
                return true;
            }
        }
    }
    return false;
};

// Get a filtered TopicMapEntry for a single topic.
const getFilteredTopic = (
    topic: TopicItem,
    topicMap: TopicMap,
    searchValue: string,
    contentTypes: readonly string[],
): TopicMapEntry => {
    return {
        ...topicMap[topic.id],
        inFilteredSet: isInFilteredSet(topic, searchValue, contentTypes),
        descendantInFilteredSet: childInFilteredSet(
            topic,
            searchValue,
            contentTypes,
        ),
    };
};

// Get a filtered TopicMapEntry for the given TopicChild and all of it
// descendant topics.
const getFilteredTopics = (
    child: TopicChild,
    topicMap: TopicMap,
    searchValue: string,
    contentTypes: readonly string[],
): Record<string, TopicMapEntry> => {
    if (child.kind && child.kind === "Topic") {
        // This child is a TopicItem. Filter it.
        const filteredTopic: Record<string, TopicMapEntry> = {
            [child.id]: getFilteredTopic(
                child,
                topicMap,
                searchValue,
                contentTypes,
            ),
        };

        // Recurse to filter this topic's children.
        const children: TopicChild[] = child.children || [];
        const filteredChildren: Record<string, TopicMapEntry> = children.reduce<
            Record<string, any>
        >(
            (map, child) => ({
                ...map,
                ...getFilteredTopics(
                    child,
                    topicMap,
                    searchValue,
                    contentTypes,
                ),
            }),
            {},
        );
        return {
            ...filteredTopic,
            ...filteredChildren,
        };
    }
    // This child is not a TopicItem.  Ignore it.
    return {};
};

export const Reducers = {
    selectedContentDescriptors(
        state: SelectedContentDescriptors = {},
        action: Action,
    ): SelectedContentDescriptors {
        switch (action.type) {
            case "CHECK_CONTENT":
                return {
                    ...state,
                    [action.qualifiedContentDescriptor]: true,
                };

            case "UNCHECK_CONTENT":
                return {
                    ...state,
                    [action.qualifiedContentDescriptor]: false,
                };

            case "CLEAR_SELECTED_CONTENTS":
                return {};

            default:
                return state;
        }
    },

    filteredContentDescriptors(
        state: FilteredContentDescriptors = {},
        action: Action,
        allState: State,
    ): FilteredContentDescriptors {
        const {domain, searchValue, selectedContentTypes, topicMap} = allState;

        switch (action.type) {
            case "FILTER":
                const rootTopic = action.topicId && topicMap[action.topicId];

                // If for some reason the topicId is invalid, return current
                // state.
                if (!rootTopic) {
                    return state;
                }

                // If search value is empty string and there are no selected
                // content types, clear the filtered set.
                if (searchValue === "" && selectedContentTypes.length === 0) {
                    return {
                        ...state,
                        ...clearFilteredContentDescriptors(state),
                    };
                }

                // Filter all content.
                const topicChildren: readonly TopicChild[] =
                    rootTopic.children || [];

                const filteredContent = topicChildren
                    .map((child) =>
                        getFilteredContentChildren(
                            rootTopic,
                            child,
                            topicMap,
                            searchValue,
                            selectedContentTypes,
                            domain,
                        ),
                    )
                    .flat();

                const newFilteredState = filteredContent.reduce<
                    Record<string, any>
                >(
                    (map, content) => ({
                        ...map,
                        [content.descriptor]: content.inFilteredSet,
                    }),
                    {},
                );
                return newFilteredState;

            default:
                return state;
        }
    },

    topicMap(state: TopicMap = {}, action: Action, allState: State): TopicMap {
        const {
            domain,
            filteredContentDescriptors,
            searchValue,
            selectedContentDescriptors,
            selectedContentTypes,
        } = allState;

        const filterIsActive: boolean = IsFilterActive(
            searchValue,
            selectedContentTypes,
        );

        switch (action.type) {
            case "RECEIVE_TOPIC_CHILDREN":
                // Recursively register all Topic children in map
                const {id, children, fetchThreeLevels} = action;
                return getTopicMapWithChildren(
                    id,
                    children,
                    null,
                    // getTopicMapWithChildren modifies the given map.
                    // Make a new map from state here.
                    {...state},
                    fetchThreeLevels,
                );

            case "CHECK_TOPIC":
                const topicToCheck = {
                    ...state[action.id],
                    selected: true,
                } as const;
                const checkedState = {
                    ...state,
                    [action.id]: topicToCheck,
                } as const;

                return {
                    ...checkedState,
                    ...getUpdatedChildren(true, action.id, checkedState),
                    ...getMaybeCheckedParents(
                        topicToCheck.parentId,
                        checkedState,
                        selectedContentDescriptors,
                        filteredContentDescriptors,
                        domain,
                        filterIsActive,
                    ),
                };

            case "UNCHECK_TOPIC":
            case "CLEAR_SELECTED_CONTENTS":
                const topicToUncheck = {
                    ...state[action.id],
                    selected: false,
                } as const;
                const uncheckedState = {
                    ...state,
                    [action.id]: topicToUncheck,
                } as const;

                return {
                    ...uncheckedState,
                    ...getUpdatedChildren(false, action.id, uncheckedState),
                    ...getUncheckedParents(
                        topicToUncheck.parentId,
                        uncheckedState,
                    ),
                };

            case "CHECK_CONTENT":
                // Since the contents don't have children, only care about
                // their parent.
                return {
                    ...state,
                    ...getMaybeCheckedParents(
                        action.parentId,
                        state,
                        selectedContentDescriptors,
                        filteredContentDescriptors,
                        domain,
                        filterIsActive,
                    ),
                };

            case "UNCHECK_CONTENT":
                return {
                    ...state,
                    ...getUncheckedParents(action.parentId, state),
                };

            case "FILTER":
                const rootTopic = action.topicId && state[action.topicId];

                // If for some reason the topicId is invalid, return the current
                // state.
                if (!rootTopic) {
                    return state;
                }

                // If search value is empty string and there are no selected
                // content types, clear the filtered set.
                if (searchValue === "" && selectedContentTypes.length === 0) {
                    return {
                        ...state,
                        ...clearTopicMapFilter(state),
                    };
                }

                // Filter all topics.
                const topicChildren: readonly TopicChild[] =
                    rootTopic.children || [];

                const filteredTopics: Record<string, TopicMapEntry> =
                    topicChildren.reduce<Record<string, any>>(
                        (map, child) => ({
                            ...map,
                            ...getFilteredTopics(
                                child,
                                state,
                                searchValue,
                                selectedContentTypes,
                            ),
                        }),
                        {},
                    );

                return {
                    ...state,
                    ...filteredTopics,
                };

            default:
                return state;
        }
    },

    domain(state: CurationNode, action: Action): CurationNode {
        switch (action.type) {
            case "RECEIVE_TOPIC_CHILDREN":
                return action.domain;
            default:
                return state;
        }
    },

    hasPreviewedContent(state = false, action: Action): boolean {
        if (action.type === "SHOW_MODAL") {
            return true;
        }
        return state;
    },

    courseFullyLoaded(state: boolean, action: Action): boolean {
        switch (action.type) {
            case "COMPLETE_COURSE_LOAD":
                return true;
            case "RESET_COURSE_LOADED":
                return false;
        }

        return state;
    },

    modalNavigator(
        state: ModalNavigator = null,
        action: Action,
    ): ModalNavigator {
        switch (action.type) {
            case "SHOW_MODAL":
                const {idx, items} = action;
                return {
                    idx,
                    items,
                };

            case "NEXT_MODAL":
                if (!state) {
                    return state;
                }

                return {
                    ...state,
                    idx: state.idx + 1,
                };

            case "PREV_MODAL":
                if (!state) {
                    return state;
                }

                return {
                    ...state,
                    idx: state.idx - 1,
                };

            case "CLOSE_MODAL":
                return null;

            default:
                return state;
        }
    },

    searchValue(state = "", action: Action): string {
        switch (action.type) {
            case "SET_SEARCH_VALUE":
                return action.searchValue;
            default:
                return state;
        }
    },

    selectedContentTypes(state: string[] = [], action: Action): string[] {
        switch (action.type) {
            case "SET_SELECTED_CONTENT_TYPES":
                return action.contentTypes;
            default:
                return state;
        }
    },
} as const;

export interface State {
    domain: CurationNode;
    selectedContentDescriptors: SelectedContentDescriptors;
    filteredContentDescriptors: FilteredContentDescriptors;
    topicMap: TopicMap;
    modalNavigator: ModalNavigator;
    hasPreviewedContent: boolean;
    courseFullyLoaded: boolean;
    searchValue: string;
    selectedContentTypes: string[];
}
